import { faArrowLeft, faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Card, HelperText, Label, Select, TextInput } from 'flowbite-react';
import { usePostHog } from 'posthog-js/react';
import { Suspense, useCallback, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router';

import type { MessageType } from 'src/api/models';
import { mutations } from 'src/api/mutations';
import { FaIconCircleInfo } from 'src/components/fa-icon-circle-info';
import { FaIconLite } from 'src/components/fa-icon-lite';
import { FileUploadDropzone } from 'src/components/file-upload-dropzone-lazy';

import { ContainerOutletContext } from './container';
import LoadingScreen from './loading-screen';

function emailScreenshotMediaFilename(filename: string, random: number) {
  return `emailScreenshot.${random}-${filename}`;
}
const FlowMessageTypes: Record<MessageType, string> = {
  ABANDONEDCART: 'Abandoned Cart',
  WELCOME: 'Welcome',
  THANKYOU: 'Thank You',
  WINBACK: 'Winback',
  ABANDONEDBROWSE: 'Abandoned Browse',
  REFILLREMINDER: 'Refill Reminder',
  UPCOMINGSUBSCRIPTION: 'Upcoming Subscription',
  CAMPAIGN: 'Campaign',
  OTHER: 'Other',
} as const;

const standardizeUrl = (url: string): string => {
  let cleanUrl = url.trim();
  cleanUrl = cleanUrl.replace(/^(https?:\/\/)?(www\.)?/, '');
  return `https://${cleanUrl}`;
};
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
const isValidStoreUrl = (storeUrl: string): boolean => {
  const storeUrlRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?::\d{1,5})?(?:\/)?$/;
  return storeUrlRegex.test(storeUrl);
};
export function OptimizerLiteDashboard() {
  const {
    optimizerLiteContext: { liteGenerationGroup },
  } = useOutletContext<ContainerOutletContext>();
  const [uploadKey, setUploadKey] = useState(0);
  const posthog = usePostHog();
  const [selectedFlowMessageType, setSelectedFlowMessageType] = useState('');
  const randomForFn = useRef(Date.now());
  const [storeContactAddressError, setStoreContactAddressError] = useState('');
  const [storeUrlError, setStoreUrlError] = useState('');
  const [customerEmailAddress, setCustomerEmailAddress] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [emailScreenshotError, setEmailScreenshotError] = useState('');
  const [pendingUploads, setPendingUploads] = useState<{ filename: string; uploadFilename: string }[]>([]);
  const uploadFilenames = useRef({ screenshotFilename: '' });
  const [updateEmailScreenshotValue, setUpdateEmailScreenshotValue] = useState(false);
  const [generationinProgress, setGenerationInProgress] = useState(false);

  const { mutateAsync: uploadUrlMutation } = useMutation(mutations.signedEmailScreenshotUploadUrl);
  const { mutateAsync: didUploadEmailScreenshotMutation, isPending: didUploadEmailScreenshotMutationIsPending } =
    useMutation(mutations.didUploadEmailScreenshot);

  const [emailIsInvalid, setEmailIsInvalid] = useState(false);
  const [storeUrlIsInvalid, setStoreUrlIsInvalid] = useState(false);

  const { mutateAsync: triggerVariantGeneration } = useMutation(mutations.triggerVariantGeneration);
  const { mutateAsync: saveSpOptimizerLiteSetCustomerEmailAndMessageType } = useMutation(
    mutations.saveSpOptimizerLiteSetCustomerEmailAndMessageType,
  );

  const [customerEmailAndFlowMessageTypeSaveIsPending, setCustomerEmailAndFlowMessageTypeSaveIsPending] =
    useState(false);

  const logEventToPosthog = useCallback(
    (eventName: string) => {
      if (!customerEmailAddress) {
        posthog.capture(eventName);
        return;
      }
      const posthogData: { email: string; messageType?: string } = {
        email: customerEmailAddress,
      };
      if (selectedFlowMessageType) {
        posthogData.messageType = selectedFlowMessageType;
      }
      posthog.capture(eventName, posthogData);
    },
    [posthog, customerEmailAddress, selectedFlowMessageType],
  );
  const launchVariantGeneration = useCallback(async () => {
    setEmailScreenshotError('');
    setStoreContactAddressError('');
    setStoreUrlError('');
    await triggerVariantGeneration([liteGenerationGroup.id]);
  }, [liteGenerationGroup, triggerVariantGeneration]);

  const saveCustomerEmailAndFlowMessageType = useCallback(async () => {
    setCustomerEmailAndFlowMessageTypeSaveIsPending(true);
    setGenerationInProgress(true);
    try {
      if (pendingUploads.length === 0) {
        return;
      }
      if (!customerEmailAddress || !selectedFlowMessageType) {
        return;
      }
      await Promise.all(
        pendingUploads.map((upload) =>
          didUploadEmailScreenshotMutation([liteGenerationGroup.id, upload.filename, upload.uploadFilename]),
        ),
      );
      setPendingUploads([]);
      await saveSpOptimizerLiteSetCustomerEmailAndMessageType([
        liteGenerationGroup.id,
        customerEmailAddress,
        selectedFlowMessageType as MessageType,
        standardizeUrl(storeUrl),
      ]);
      await launchVariantGeneration();
      window.location.replace(`/optimizer-lite/generations/${liteGenerationGroup.id}`);
    } catch (err) {
      console.error('generation error: ', err);
      setCustomerEmailAndFlowMessageTypeSaveIsPending(false);
      setGenerationInProgress(false);
    }
  }, [
    didUploadEmailScreenshotMutation,
    customerEmailAddress,
    pendingUploads,
    liteGenerationGroup,
    launchVariantGeneration,
    selectedFlowMessageType,
    storeUrl,
    saveSpOptimizerLiteSetCustomerEmailAndMessageType,
  ]);

  return (
    <>
      <div className='flex min-h-screen w-full items-center justify-center overflow-hidden pt-14'>
        {generationinProgress ? (
          <LoadingScreen />
        ) : (
          <Card>
            <Link
              to='/optimizer-lite'
              className='left-4 top-4 flex cursor-pointer items-center gap-1 text-sm text-black hover:text-gray-900'
            >
              <FaIconLite icon={faArrowLeft} className='size-3' />
              <span>Back</span>
            </Link>
            <div id='create-store-content' className='mx-10 mt-5 flex w-96 flex-col'>
              <div className='mb-6 flex w-72 items-center justify-center place-self-center'>
                <div className='w-full'>
                  <Label className='mb-3 block text-sm font-medium'>
                    Upload a screenshot of the email you want to generate a subject line/preview for
                  </Label>
                  {emailScreenshotError && (
                    <Alert color='failure' icon={FaIconCircleInfo}>
                      <span>
                        <p>
                          <span className='font-medium'>A screenshot of your email is required!</span>{' '}
                          {emailScreenshotError}
                        </p>
                      </span>
                    </Alert>
                  )}
                  <Suspense>
                    <div className='relative w-full'>
                      <FileUploadDropzone
                        key={uploadKey}
                        subtitle='Upload a JPG or PNG file'
                        showPreview={true}
                        allowedExtensions={['jpg', 'jpeg', 'png']}
                        accept='image/jpg,image/jpeg,image/png'
                        method='PUT'
                        allowReplacingExistingFile={false}
                        existingFile={pendingUploads[0]?.filename}
                        hasExistingFile={false}
                        uploadUrl={async (fn) => {
                          setEmailScreenshotError('');
                          const filename = emailScreenshotMediaFilename(fn, randomForFn.current);
                          const { signedUrl, uploadFilename } = await uploadUrlMutation([
                            liteGenerationGroup.id,
                            filename,
                          ]);
                          uploadFilenames.current.screenshotFilename = uploadFilename;
                          return signedUrl;
                        }}
                        onStart={() => Promise.resolve(true)}
                        onComplete={(fn) => {
                          setPendingUploads((current) => [
                            ...current,
                            {
                              filename: emailScreenshotMediaFilename(fn, randomForFn.current),
                              uploadFilename: uploadFilenames.current.screenshotFilename,
                            },
                          ]);
                          setUpdateEmailScreenshotValue(true);
                          return Promise.resolve(true);
                        }}
                      />
                      {updateEmailScreenshotValue && (
                        <button
                          onClick={() => {
                            setPendingUploads([]);
                            setUpdateEmailScreenshotValue(false);
                            setUploadKey((prev) => prev + 1);
                          }}
                          className='absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                        >
                          <FaIconLite icon={faXmark} className='size-3' />
                        </button>
                      )}
                    </div>
                  </Suspense>
                </div>
              </div>

              <div className='mx-10 mb-5'>
                <div className='w-full'>
                  <div>
                    <div>
                      <Label
                        color={storeContactAddressError ? 'failure' : 'gray'}
                        htmlFor='companyName'
                        className='mb-2 block text-sm font-medium'
                      >
                        Select Type of Email <span className='text-base text-rose-600'>*</span>
                      </Label>
                      <Select
                        id='select-flow-message-type'
                        required
                        className='inline-flex w-full'
                        onChange={(e) => {
                          setSelectedFlowMessageType(e.target.value);
                        }}
                      >
                        <option value={undefined}>--- Choose ---</option>
                        {Object.entries(FlowMessageTypes).map(([type, label]) => (
                          <option value={type} key={type}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <div className='my-6'>
                    <>
                      <Label
                        color={storeContactAddressError ? 'failure' : 'gray'}
                        htmlFor='companyName'
                        className='mb-2 block text-sm font-medium'
                      >
                        Enter your email address <span className='text-base text-rose-600'>*</span>
                      </Label>
                      <TextInput
                        color={storeContactAddressError ? 'failure' : 'gray'}
                        sizing='sm'
                        id='customerEmailAddress'
                        onBlur={(e) => {
                          const email = e.target.value;
                          if (email) {
                            setEmailIsInvalid(!isValidEmail(email));
                          }
                        }}
                        placeholder='johndoe@workemail.com'
                        value={customerEmailAddress}
                        onChange={(e) => {
                          if (emailIsInvalid) {
                            setEmailIsInvalid(false);
                          }
                          setCustomerEmailAddress(e.target.value);
                        }}
                        required
                      />
                      {storeContactAddressError ? (
                        <HelperText color='failure'>
                          <span className='font-medium'>Oops!</span> {storeContactAddressError}
                        </HelperText>
                      ) : emailIsInvalid ? (
                        <HelperText color='failure'>Please enter a valid email address</HelperText>
                      ) : null}
                    </>
                  </div>
                  <div className='my-6'>
                    <>
                      <Label
                        color={storeUrlError ? 'failure' : 'gray'}
                        htmlFor='storeUrl'
                        className='mb-2 block text-sm font-medium'
                      >
                        Enter your store url{' '}
                        <span className='font-light'>(this helps the AI best understand/reflect your brand)</span>{' '}
                        <span className='text-base text-rose-600'>*</span>
                      </Label>
                      <div className='relative flex w-full items-center'>
                        <TextInput
                          color={storeUrlError ? 'failure' : 'gray'}
                          sizing='sm'
                          id='customerEmailAddress'
                          className='w-full'
                          onBlur={(e) => {
                            const storeDomain = e.target.value;
                            if (storeDomain) {
                              setStoreUrlIsInvalid(!isValidStoreUrl(storeDomain));
                            }
                          }}
                          placeholder='mystore.com'
                          value={storeUrl}
                          onChange={(e) => {
                            if (storeUrlIsInvalid) {
                              setStoreUrlIsInvalid(false);
                            }
                            setStoreUrl(e.target.value);
                          }}
                          required
                        />
                        {storeUrlError ? (
                          <HelperText color='failure'>
                            <span className='font-medium'>Oops!</span> {storeUrlError}
                          </HelperText>
                        ) : storeUrlIsInvalid ? (
                          <HelperText color='failure'>Please enter a valid store URL</HelperText>
                        ) : null}
                      </div>
                    </>
                  </div>
                </div>
              </div>
              <div className='flex w-full justify-center'>
                <Button
                  className='mb-2 w-52 rounded-lg bg-linear-to-br from-purple-600 to-blue-500 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-linear-to-bl focus:outline-none focus:ring-4 focus:ring-blue-300'
                  disabled={
                    emailIsInvalid ||
                    !updateEmailScreenshotValue ||
                    customerEmailAddress === '' ||
                    selectedFlowMessageType === ''
                  }
                  onClick={() => {
                    logEventToPosthog('optimizerLiteGenerateSubjectandPreviewClicked');
                    void saveCustomerEmailAndFlowMessageType();
                  }}
                >
                  {didUploadEmailScreenshotMutationIsPending || customerEmailAndFlowMessageTypeSaveIsPending ? (
                    <FaIconLite icon={faSpinner} spin className='mr-3' />
                  ) : (
                    <div>Generate Subject and Preview!</div>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
